import {ViewedFileProgress} from './ViewedFileProgress'
import {OpenCommentsSidePanelButton} from './OpenCommentsSidePanelButton'
import {OpenAnnotationsPanelButton} from './OpenAnnotationsPanelButton'
import {ReviewMenuButton} from './ReviewMenuButton'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import {RefreshButton} from './RefreshButton'
import {Stack} from '@primer/react'
import {ChangesSelector} from '../diff-filtering/ChangesSelector'
import type {FilesRoutePayload} from '../../page-data/payloads/files'
import type {ViewedFilesCountPayload} from '../../page-data/payloads/viewed-files-count'
import type {CommentingImplementation} from '@github-ui/conversations'
import {FileFilter, type FileFilterMenuOptions, type FileFilterState} from '../diff-filtering/FileFilter'
import {Suspense} from 'react'

export type PullRequestFilesToolbarProps = {
  annotations: FilesRoutePayload['annotations']
  baseRefOid: string
  commentBoxConfig: CommentingImplementation['commentBoxConfig']
  commits: FilesRoutePayload['commits']
  currentUserLogin?: string
  isFileTreeExpanded?: boolean
  lastReviewOid?: string
  pullRequest: FilesRoutePayload['pullRequest']
  repository: FilesRoutePayload['repository']
  shouldShowViewedFilesCount: boolean
  threadPreviews: FilesRoutePayload['threadPreviews']
  totalFilesCount: number
  treeToggleElement?: JSX.Element
  viewedFilesCount: ViewedFilesCountPayload['viewedFilesCount']
} & {
  fileFilterMenuOptions: FileFilterMenuOptions
  fileFilterState: FileFilterState
  setFileFilterState: (state: FileFilterState) => void
}

export function PullRequestFilesToolbar({
  annotations,
  baseRefOid,
  commentBoxConfig,
  commits,
  currentUserLogin,
  fileFilterMenuOptions,
  fileFilterState,
  isFileTreeExpanded,
  lastReviewOid,
  pullRequest,
  repository,
  setFileFilterState,
  shouldShowViewedFilesCount,
  threadPreviews,
  totalFilesCount,
  treeToggleElement,
  viewedFilesCount,
}: PullRequestFilesToolbarProps) {
  return (
    <section
      className="d-flex flex-justify-between flex-items-center bgColor-default py-3"
      id="react-partial-pull-requests-files-toolbar"
    >
      <h2 className="sr-only">Pull Request Toolbar</h2>
      <Stack direction="horizontal" gap="condensed">
        <div>{treeToggleElement}</div>
        <ChangesSelector
          baseRefOid={baseRefOid}
          commits={commits}
          lastReviewOid={lastReviewOid}
          ownerLogin={repository.ownerLogin}
          pullRequestNumber={pullRequest.number}
          repositoryName={repository.name}
        />
        {shouldShowViewedFilesCount && (
          <ViewedFileProgress
            pullRequest={pullRequest}
            totalFilesCount={totalFilesCount}
            viewedFilesCount={viewedFilesCount}
          />
        )}
      </Stack>
      <div className="d-flex flex-items-center gap-2">
        <RefreshButton aliveChannel={pullRequest.aliveChannel} pathName={pullRequest.pathName} />

        {!isFileTreeExpanded && (
          <FileFilter
            diffs={[]}
            fileFilterMenuOptions={fileFilterMenuOptions}
            fileFilterState={fileFilterState}
            filterSize="small"
            setFileFilterState={setFileFilterState}
          />
        )}

        <OpenCommentsSidePanelButton
          pullRequest={pullRequest}
          threadPreviews={threadPreviews}
          repositoryId={repository.id}
        />
        <OpenAnnotationsPanelButton annotations={annotations} />
        {currentUserLogin && (
          // TODO: move suspense and suspenseQuery usage to the dialog rather than the button itself
          <Suspense>
            <ReviewMenuButton
              commentBoxConfig={commentBoxConfig}
              currentUserLogin={currentUserLogin}
              pullRequest={pullRequest}
              repository={repository}
            />
          </Suspense>
        )}
        <DiffViewSettings invisible={false} reloadOnWhitespaceChange small />
      </div>
    </section>
  )
}

try{ PullRequestFilesToolbar.displayName ||= 'PullRequestFilesToolbar' } catch {}